import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  IconButton,
  InputAdornment,
  Snackbar,
  Tabs,
  Tab,
  Grid,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  getBudgetPlanYears,
  getBudgetPlan,
  createBudgetPlan,
  deleteBudgetPlan,
  updatePlanItem,
  addPlanOverride,
  removePlanOverride,
  getMonthlyTracking,
  getYearlyTracking,
  updatePlanStatus,
} from '../services/budgetPlan';

const SECTION_COLORS = {
  INCOME: '#e8f5e9',
  TAXES: '#fff3e0',
  SAVINGS: '#e3f2fd',
  EXPENSES: '#fce4ec',
  'BUDGET SUMMARY': '#f3e5f5',
};

const SECTION_LABELS = {
  INCOME: 'Income',
  TAXES: 'Taxes',
  SAVINGS: 'Savings',
  EXPENSES: 'Expenses',
  'BUDGET SUMMARY': 'Budget Summary',
};

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_SHORT = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(val);
}

export default function BudgetPlan() {
  const [planYears, setPlanYears] = useState([]);
  const [sourceYears, setSourceYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planSections, setPlanSections] = useState([]);
  const [trackingData, setTrackingData] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [selectedTrackingMonth, setSelectedTrackingMonth] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [createDialog, setCreateDialog] = useState(false);
  const [createSourceYear, setCreateSourceYear] = useState('');
  const [createTargetYear, setCreateTargetYear] = useState('');
  const [overrideDialog, setOverrideDialog] = useState({
    open: false, item: null, month: '', amount: '', reason: '',
  });
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Load initial data
  useEffect(() => {
    async function init() {
      try {
        const res = await getBudgetPlanYears();
        const { plan_years, source_years } = res.data;
        setPlanYears(plan_years);
        setSourceYears(source_years);
        if (plan_years.length > 0) {
          setSelectedYear(plan_years[0]);
        } else if (source_years.length > 0) {
          setSelectedYear(source_years[0]);
        }
      } catch {
        // No data
      } finally {
        setLoading(false);
      }
    }
    init();
  }, []);

  const loadPlan = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const res = await getBudgetPlan(selectedYear);
      setPlan(res.data.plan);
      setPlanSections(res.data.sections);
    } catch {
      setPlan(null);
      setPlanSections([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  const loadTracking = useCallback(async () => {
    if (!selectedYear || !selectedTrackingMonth) return;
    setLoading(true);
    try {
      const res = await getMonthlyTracking(selectedYear, selectedTrackingMonth);
      setTrackingData(res.data);
    } catch {
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedTrackingMonth]);

  const loadYearly = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const res = await getYearlyTracking(selectedYear);
      setYearlyData(res.data);
    } catch {
      setYearlyData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  // Load data when year or tab changes
  useEffect(() => {
    if (!selectedYear) return;
    if (tabIndex === 0) loadPlan();
    else if (tabIndex === 1 && selectedTrackingMonth) loadTracking();
    else if (tabIndex === 2) loadYearly();
  }, [selectedYear, tabIndex, loadPlan, loadTracking, loadYearly, selectedTrackingMonth]);

  const handleCreatePlan = async () => {
    setCreateDialog(false);
    try {
      await createBudgetPlan({
        year: parseInt(createTargetYear),
        source_year: parseInt(createSourceYear),
      });
      setSnackbar({ open: true, message: 'Budget plan created!' });
      // Refresh years list and load the new plan
      const res = await getBudgetPlanYears();
      setPlanYears(res.data.plan_years);
      setSourceYears(res.data.source_years);
      setSelectedYear(parseInt(createTargetYear));
      setTabIndex(0);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create plan';
      setSnackbar({ open: true, message: msg });
    }
  };

  const handleDeletePlan = async () => {
    setDeleteDialog(false);
    try {
      await deleteBudgetPlan(selectedYear);
      setSnackbar({ open: true, message: 'Plan deleted' });
      setPlan(null);
      setPlanSections([]);
      const res = await getBudgetPlanYears();
      setPlanYears(res.data.plan_years);
      setSourceYears(res.data.source_years);
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete plan' });
    }
  };

  const handleSaveItem = async (itemId, newAmount, notes) => {
    try {
      await updatePlanItem(selectedYear, itemId, {
        planned_amount: parseFloat(newAmount),
        ...(notes !== undefined && { notes }),
      });
      setSnackbar({ open: true, message: 'Baseline updated' });
      loadPlan();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save' });
    }
  };

  const handleSaveTrackingItem = async (itemId, newPlannedAmount) => {
    try {
      await addPlanOverride(selectedYear, {
        item_id: itemId,
        month: selectedTrackingMonth,
        override_amount: parseFloat(newPlannedAmount),
        reason: 'Monthly adjustment',
      });
      setSnackbar({ open: true, message: 'Monthly plan updated' });
      loadTracking();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save monthly adjustment' });
    }
  };

  const handleSaveOverride = async () => {
    const { item, month, amount, reason } = overrideDialog;
    setOverrideDialog({ open: false, item: null, month: '', amount: '', reason: '' });
    try {
      await addPlanOverride(selectedYear, {
        item_id: item.id,
        month: `${selectedYear}-${month}`,
        override_amount: parseFloat(amount),
        reason,
      });
      setSnackbar({ open: true, message: `Override saved for ${MONTH_NAMES[parseInt(month)]}` });
      loadPlan();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save override' });
    }
  };

  const handleRemoveOverride = async (overrideId) => {
    try {
      await removePlanOverride(selectedYear, overrideId);
      setSnackbar({ open: true, message: 'Override removed' });
      loadPlan();
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove override' });
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = plan.status === 'active' ? 'draft' : 'active';
    try {
      await updatePlanStatus(selectedYear, newStatus);
      setSnackbar({ open: true, message: `Plan ${newStatus === 'active' ? 'activated' : 'set to draft'}` });
      loadPlan();
    } catch {
      setSnackbar({ open: true, message: 'Failed to update status' });
    }
  };

  // All possible years to show in the selector
  const allYears = [...new Set([...planYears, ...sourceYears])].sort((a, b) => b - a);

  if (loading && allYears.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (allYears.length === 0) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>Budget Plan</Typography>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No budget data yet. Upload Excel files on the Upload page first,
              then create a budget plan from your historical data.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const hasPlan = plan != null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">Budget Plan</Typography>
          {hasPlan && (
            <Chip
              label={plan.status === 'active' ? 'Active' : 'Draft'}
              color={plan.status === 'active' ? 'success' : 'default'}
              size="small"
              onClick={handleToggleStatus}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!hasPlan && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateTargetYear(String(selectedYear || sourceYears[0] || new Date().getFullYear()));
                setCreateSourceYear(String(sourceYears[0] || ''));
                setCreateDialog(true);
              }}
            >
              Create Plan
            </Button>
          )}
          {hasPlan && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialog(true)}
            >
              Delete Plan
            </Button>
          )}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear || ''}
              label="Year"
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setTrackingData(null);
                setYearlyData(null);
              }}
            >
              {allYears.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Tabs */}
      {hasPlan && (
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Plan Setup" />
          <Tab label="Monthly Tracking" />
          <Tab label="Yearly Overview" />
        </Tabs>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : !hasPlan ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No budget plan for {selectedYear}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a plan from your historical budget averages to set monthly targets
              for income, expenses, savings, and taxes.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateTargetYear(String(selectedYear || sourceYears[0] || new Date().getFullYear()));
                setCreateSourceYear(String(sourceYears[0] || ''));
                setCreateDialog(true);
              }}
            >
              Create Budget Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tab 0: Plan Setup */}
          {tabIndex === 0 && (
            <>
              <PlanNetTracker sections={planSections} />
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Based on {plan.source_month_count} months of {plan.source_year} data.
                  Edit baseline amounts to set your monthly targets. Add overrides for months
                  that differ from the baseline (e.g., holiday spending in December).
                </Typography>
              </Alert>
              {planSections.map((section) => (
                <Accordion key={section.section} defaultExpanded sx={{ mb: 1 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: SECTION_COLORS[section.section] || '#f5f5f5' }}
                  >
                    <Typography variant="h6">
                      {SECTION_LABELS[section.section] || section.section}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Monthly Baseline</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Annual Total</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, width: 100 }}>Overrides</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>Edit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.items.map((item, idx) => (
                            <PlanItemRow
                              key={item.id || `t-${idx}`}
                              item={item}
                              selectedYear={selectedYear}
                              onSave={handleSaveItem}
                              onOverride={(item) => setOverrideDialog({
                                open: true,
                                item,
                                month: '',
                                amount: String(item.planned_amount || 0),
                                reason: '',
                              })}
                              onRemoveOverride={handleRemoveOverride}
                            />
                          ))}
                          {section.groups.map((group) => (
                            <React.Fragment key={group.parent_category}>
                              <TableRow>
                                <TableCell
                                  colSpan={5}
                                  sx={{
                                    fontWeight: 700,
                                    backgroundColor: '#f5f5f5',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    letterSpacing: 1,
                                  }}
                                >
                                  {group.parent_category}
                                </TableCell>
                              </TableRow>
                              {group.items.map((item, idx) => (
                                <PlanItemRow
                                  key={item.id || `g-${group.parent_category}-${idx}`}
                                  item={item}
                                  selectedYear={selectedYear}
                                  onSave={handleSaveItem}
                                  onOverride={(item) => setOverrideDialog({
                                    open: true,
                                    item,
                                    month: '',
                                    amount: String(item.planned_amount || 0),
                                    reason: '',
                                  })}
                                  onRemoveOverride={handleRemoveOverride}
                                />
                              ))}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}

          {/* Tab 1: Monthly Tracking */}
          {tabIndex === 1 && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={selectedTrackingMonth}
                    label="Month"
                    onChange={(e) => setSelectedTrackingMonth(e.target.value)}
                  >
                    {Array.from({ length: 12 }, (_, i) => {
                      const m = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                      return (
                        <MenuItem key={m} value={m}>
                          {MONTH_NAMES[i + 1]} {selectedYear}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>

              {!selectedTrackingMonth ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      Select a month to see plan vs actual comparison.
                    </Typography>
                  </CardContent>
                </Card>
              ) : trackingData ? (
                <>
                  <TrackingNetTracker sections={trackingData.sections} />
                  {!trackingData.has_actuals && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      No actual data uploaded for this month yet. Showing planned amounts only.
                    </Alert>
                  )}
                  {trackingData.sections.map((section) => (
                    <Accordion key={section.section} defaultExpanded sx={{ mb: 1 }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ backgroundColor: SECTION_COLORS[section.section] || '#f5f5f5' }}
                      >
                        <Typography variant="h6">
                          {SECTION_LABELS[section.section] || section.section}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Planned</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actual</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Variance</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Var %</TableCell>
                                <TableCell sx={{ width: 48 }} />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {section.items.map((item, idx) => (
                                <EditableTrackingRow key={idx} item={item} onSave={handleSaveTrackingItem} />
                              ))}
                              {section.groups.map((group) => (
                                <React.Fragment key={group.parent_category}>
                                  <TableRow>
                                    <TableCell
                                      colSpan={6}
                                      sx={{
                                        fontWeight: 700,
                                        backgroundColor: '#f5f5f5',
                                        textTransform: 'uppercase',
                                        fontSize: '0.8rem',
                                        letterSpacing: 1,
                                      }}
                                    >
                                      {group.parent_category}
                                    </TableCell>
                                  </TableRow>
                                  {group.items.map((item, idx) => (
                                    <EditableTrackingRow key={idx} item={item} onSave={handleSaveTrackingItem} />
                                  ))}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              ) : null}
            </>
          )}

          {/* Tab 2: Yearly Overview */}
          {tabIndex === 2 && yearlyData && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {['INCOME', 'EXPENSES', 'SAVINGS', 'TAXES'].map((sec) => {
                  const ytd = yearlyData.ytd[sec] || {};
                  const planned = ytd.planned || 0;
                  const actual = ytd.actual || 0;
                  const pct = planned ? Math.round((actual / planned) * 100) : 0;
                  return (
                    <Grid item xs={12} sm={6} md={3} key={sec}>
                      <Card>
                        <CardContent>
                          <Typography variant="caption" color="text.secondary">
                            {SECTION_LABELS[sec]} (YTD)
                          </Typography>
                          <Typography variant="h6">
                            {formatCurrency(actual)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            of {formatCurrency(planned)} planned
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(pct, 100)}
                            color={
                              sec === 'INCOME' || sec === 'SAVINGS'
                                ? (pct >= 100 ? 'success' : 'primary')
                                : (pct > 100 ? 'error' : 'success')
                            }
                            sx={{ mt: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {pct}%
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Monthly Bar Chart */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Monthly Planned vs Actual
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={yearlyData.monthly.map((m) => ({
                      month: MONTH_SHORT[parseInt(m.month.split('-')[1])],
                      Planned: Math.abs(m.sections.EXPENSES?.planned || 0)
                        + Math.abs(m.sections.TAXES?.planned || 0)
                        + Math.abs(m.sections.SAVINGS?.planned || 0),
                      Actual: m.has_actuals
                        ? Math.abs(m.sections.EXPENSES?.actual || 0)
                          + Math.abs(m.sections.TAXES?.actual || 0)
                          + Math.abs(m.sections.SAVINGS?.actual || 0)
                        : 0,
                      Income_Planned: m.sections.INCOME?.planned || 0,
                      Income_Actual: m.has_actuals ? (m.sections.INCOME?.actual || 0) : 0,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <RechartsTooltip
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="Income_Planned" name="Income (Plan)" fill="#81c784" />
                      <Bar dataKey="Income_Actual" name="Income (Actual)" fill="#2e7d32" />
                      <Bar dataKey="Planned" name="Outflow (Plan)" fill="#ef9a9a" />
                      <Bar dataKey="Actual" name="Outflow (Actual)" fill="#c62828" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Section Summary Table */}
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Section Summary
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Annual Planned</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>YTD Actual</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>YTD Variance</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Months w/ Data</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {['INCOME', 'TAXES', 'SAVINGS', 'EXPENSES'].map((sec) => {
                          const ytd = yearlyData.ytd[sec] || {};
                          const annualPlanned = (yearlyData.monthly || []).reduce(
                            (sum, m) => sum + (m.sections[sec]?.planned || 0), 0
                          );
                          const actual = ytd.actual || 0;
                          const isFavorable = sec === 'INCOME' || sec === 'SAVINGS'
                            ? actual >= ytd.planned
                            : actual <= ytd.planned;
                          const variance = sec === 'INCOME' || sec === 'SAVINGS'
                            ? actual - (ytd.planned || 0)
                            : (ytd.planned || 0) - actual;
                          return (
                            <TableRow key={sec}>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={SECTION_LABELS[sec]}
                                  sx={{
                                    backgroundColor: SECTION_COLORS[sec],
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(annualPlanned)}</TableCell>
                              <TableCell align="right">{formatCurrency(actual)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: isFavorable ? 'success.main' : 'error.main', fontWeight: 600 }}
                              >
                                {formatCurrency(variance)}
                              </TableCell>
                              <TableCell align="center">
                                {yearlyData.months_with_data?.length || 0} / 12
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Create Plan Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Budget Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Create a budget plan by importing your historical budget averages as baseline targets.
            You can adjust individual amounts after creation.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Plan Year</InputLabel>
            <Select
              value={createTargetYear}
              label="Plan Year"
              onChange={(e) => setCreateTargetYear(e.target.value)}
            >
              {[...new Set([
                new Date().getFullYear(),
                new Date().getFullYear() + 1,
                ...sourceYears,
              ])].sort((a, b) => b - a).map((y) => (
                <MenuItem key={y} value={String(y)}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Source Year (for averages)</InputLabel>
            <Select
              value={createSourceYear}
              label="Source Year (for averages)"
              onChange={(e) => setCreateSourceYear(e.target.value)}
            >
              {sourceYears.map((y) => (
                <MenuItem key={y} value={String(y)}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreatePlan}
            variant="contained"
            disabled={!createTargetYear || !createSourceYear}
          >
            Create Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override Dialog */}
      <Dialog
        open={overrideDialog.open}
        onClose={() => setOverrideDialog({ open: false, item: null, month: '', amount: '', reason: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Monthly Override — {overrideDialog.item?.category}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Set a different amount for a specific month. The baseline of{' '}
            {formatCurrency(overrideDialog.item?.planned_amount)} will be used for all other months.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={overrideDialog.month}
              label="Month"
              onChange={(e) => setOverrideDialog((prev) => ({ ...prev, month: e.target.value }))}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                  {MONTH_NAMES[i + 1]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Override Amount"
            fullWidth
            value={overrideDialog.amount}
            onChange={(e) => setOverrideDialog((prev) => ({ ...prev, amount: e.target.value }))}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <TextField
            label="Reason (optional)"
            fullWidth
            value={overrideDialog.reason}
            onChange={(e) => setOverrideDialog((prev) => ({ ...prev, reason: e.target.value }))}
            placeholder="e.g., Holiday spending, seasonal adjustment"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialog({ open: false, item: null, month: '', amount: '', reason: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveOverride}
            variant="contained"
            disabled={!overrideDialog.month || !overrideDialog.amount}
          >
            Save Override
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Budget Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the {selectedYear} budget plan?
            This will remove all plan items and overrides. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeletePlan} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}


/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function PlanItemRow({ item, selectedYear, onSave, onOverride, onRemoveOverride }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const isSubtotal = item.is_subtotal;
  const isTotal = item.is_total;
  const isEditable = !isSubtotal && !isTotal && item.id != null;

  const startEditing = () => {
    setEditValue(item.planned_amount != null ? String(item.planned_amount) : '0');
    setEditing(true);
  };

  const handleSave = () => {
    const val = parseFloat(editValue);
    if (!isNaN(val)) {
      onSave(item.id, val);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  const overrideCount = item.override_count || 0;
  const overrides = item.overrides || {};

  return (
    <TableRow
      sx={{
        ...(isSubtotal && { backgroundColor: '#fafafa' }),
        ...(isTotal && { backgroundColor: '#eeeeee' }),
        '&:hover .edit-btn': { opacity: 1 },
      }}
    >
      <TableCell
        sx={{
          pl: isSubtotal || isTotal ? 2 : 4,
          fontWeight: isSubtotal || isTotal ? 700 : 400,
          fontStyle: isSubtotal ? 'italic' : 'normal',
        }}
      >
        {item.category}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: isSubtotal || isTotal ? 700 : 400 }}>
        {editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <TextField
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              sx={{ width: 120 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <IconButton size="small" color="success" onClick={handleSave}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setEditing(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          formatCurrency(item.planned_amount)
        )}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: isSubtotal || isTotal ? 700 : 400 }}>
        {formatCurrency(item.annual_total)}
      </TableCell>
      <TableCell align="center">
        {!isSubtotal && !isTotal && overrideCount > 0 ? (
          <Tooltip
            title={
              <Box>
                {Object.entries(overrides).map(([month, ov]) => (
                  <Box key={month}>
                    {MONTH_SHORT[parseInt(month.split('-')[1])]}: {formatCurrency(ov.override_amount)}
                    {ov.reason && ` — ${ov.reason}`}
                  </Box>
                ))}
              </Box>
            }
          >
            <Chip
              size="small"
              label={`${overrideCount} override${overrideCount !== 1 ? 's' : ''}`}
              color="info"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Tooltip>
        ) : !isSubtotal && !isTotal ? (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ) : null}
      </TableCell>
      <TableCell align="center">
        {isEditable && !editing && (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <IconButton
              size="small"
              className="edit-btn"
              sx={{ opacity: 0, transition: 'opacity 0.2s' }}
              onClick={startEditing}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton
              size="small"
              className="edit-btn"
              sx={{ opacity: 0, transition: 'opacity 0.2s' }}
              onClick={() => onOverride(item)}
            >
              <TuneIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}


function EditableTrackingRow({ item, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const isSubtotal = item.is_subtotal;
  const isTotal = item.is_total;
  const isEditable = !isSubtotal && !isTotal && item.id != null && !item.unplanned;
  const variance = item.variance || 0;
  const isFavorable = variance >= 0;

  const startEditing = () => {
    setEditValue(item.planned != null ? String(item.planned) : '0');
    setEditing(true);
  };

  const handleSave = () => {
    const val = parseFloat(editValue);
    if (!isNaN(val)) {
      onSave(item.id, val);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <TableRow
      sx={{
        ...(isSubtotal && { backgroundColor: '#fafafa' }),
        ...(isTotal && { backgroundColor: '#eeeeee' }),
        '&:hover .edit-btn': { opacity: 1 },
      }}
    >
      <TableCell
        sx={{
          pl: isSubtotal || isTotal ? 2 : 4,
          fontWeight: isSubtotal || isTotal ? 700 : 400,
          fontStyle: isSubtotal ? 'italic' : 'normal',
        }}
      >
        {item.category}
        {item.has_override && (
          <Tooltip title="Has monthly override">
            <TuneIcon sx={{ fontSize: 12, ml: 0.5, color: 'info.main', verticalAlign: 'middle' }} />
          </Tooltip>
        )}
        {item.unplanned && (
          <Chip size="small" label="Unplanned" color="warning" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem', height: 18 }} />
        )}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: isSubtotal || isTotal ? 700 : 400 }}>
        {editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <TextField
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              sx={{ width: 120 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <IconButton size="small" color="success" onClick={handleSave}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setEditing(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          formatCurrency(item.planned)
        )}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: isSubtotal || isTotal ? 700 : 400 }}>
        {formatCurrency(item.actual)}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontWeight: isSubtotal || isTotal ? 700 : 400,
          color: variance === 0 ? 'text.primary' : isFavorable ? 'success.main' : 'error.main',
        }}
      >
        {variance > 0 && '+'}{formatCurrency(variance)}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          color: variance === 0 ? 'text.primary' : isFavorable ? 'success.main' : 'error.main',
        }}
      >
        {item.variance_pct != null ? `${item.variance_pct > 0 ? '+' : ''}${item.variance_pct}%` : '—'}
      </TableCell>
      <TableCell align="center" sx={{ width: 48 }}>
        {isEditable && !editing && (
          <IconButton
            size="small"
            className="edit-btn"
            sx={{ opacity: 0, transition: 'opacity 0.2s' }}
            onClick={startEditing}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}


function TrackingNetTracker({ sections }) {
  // Compute totals from tracking sections — uses 'planned' field
  const sectionTotals = { INCOME: 0, EXPENSES: 0, SAVINGS: 0 };

  for (const section of sections) {
    const key = section.section;
    if (!(key in sectionTotals)) continue;

    const sumItems = (items) => {
      for (const item of items) {
        if (!item.is_subtotal && !item.is_total && item.planned != null) {
          sectionTotals[key] += item.planned;
        }
      }
    };

    sumItems(section.items || []);
    for (const group of section.groups || []) {
      sumItems(group.items || []);
    }
  }

  const income = sectionTotals.INCOME;
  const outflow = sectionTotals.EXPENSES + sectionTotals.SAVINGS;
  const net = income - outflow;
  const isPositive = net >= 0;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'sticky',
        top: 64,
        zIndex: 10,
        mb: 3,
        px: 4,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 3,
        backgroundColor: isPositive ? '#e8f5e9' : '#ffebee',
        borderLeft: '6px solid',
        borderColor: isPositive ? 'success.main' : 'error.main',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
            Income
          </Typography>
          <Typography variant="h5" fontWeight={700} color="success.dark">
            {formatCurrency(income)}
          </Typography>
        </Box>
        <Typography variant="h4" color="text.secondary" sx={{ mx: -1 }}>−</Typography>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
            Expenses
          </Typography>
          <Typography variant="h5" fontWeight={700} color="error.dark">
            {formatCurrency(sectionTotals.EXPENSES)}
          </Typography>
        </Box>
        <Typography variant="h4" color="text.secondary" sx={{ mx: -1 }}>−</Typography>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
            Savings
          </Typography>
          <Typography variant="h5" fontWeight={700} color="info.dark">
            {formatCurrency(sectionTotals.SAVINGS)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h4" color="text.secondary">=</Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
            Monthly Net
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={isPositive ? 'success.dark' : 'error.dark'}
          >
            {net > 0 && '+'}{formatCurrency(net)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}


function PlanNetTracker({ sections }) {
  // Compute totals from plan sections — sum non-subtotal, non-total line items
  const sectionTotals = { INCOME: 0, EXPENSES: 0, SAVINGS: 0 };

  for (const section of sections) {
    const key = section.section;
    if (!(key in sectionTotals)) continue;

    const sumItems = (items) => {
      for (const item of items) {
        if (!item.is_subtotal && !item.is_total && item.planned_amount != null) {
          sectionTotals[key] += item.planned_amount;
        }
      }
    };

    sumItems(section.items || []);
    for (const group of section.groups || []) {
      sumItems(group.items || []);
    }
  }

  const income = sectionTotals.INCOME;
  const outflow = sectionTotals.EXPENSES + sectionTotals.SAVINGS;
  const net = income - outflow;
  const isPositive = net >= 0;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'sticky',
        top: 64,
        zIndex: 10,
        mb: 3,
        px: 4,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 3,
        backgroundColor: isPositive ? '#e8f5e9' : '#ffebee',
        borderLeft: '6px solid',
        borderColor: isPositive ? 'success.main' : 'error.main',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
            Income
          </Typography>
          <Typography variant="h5" fontWeight={700} color="success.dark">
            {formatCurrency(income)}
          </Typography>
        </Box>
        <Typography variant="h4" color="text.secondary" sx={{ mx: -1 }}>−</Typography>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
            Expenses
          </Typography>
          <Typography variant="h5" fontWeight={700} color="error.dark">
            {formatCurrency(sectionTotals.EXPENSES)}
          </Typography>
        </Box>
        <Typography variant="h4" color="text.secondary" sx={{ mx: -1 }}>−</Typography>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
            Savings
          </Typography>
          <Typography variant="h5" fontWeight={700} color="info.dark">
            {formatCurrency(sectionTotals.SAVINGS)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h4" color="text.secondary">=</Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
            Monthly Net
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={isPositive ? 'success.dark' : 'error.dark'}
          >
            {net > 0 && '+'}{formatCurrency(net)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
